/**
 * RoeCreate — exact copy of Knit's CREATE ROE form: Company Information,
 * Employee Information, Insurable Earnings (PP1–PP30 grid + One-click
 * Autofill from the app's own paystubs), and Separation Payments.
 * Save Draft / Save & Review persist to the employee's ROE drafts.
 */
import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { ChevronLeft, Plus, X } from "lucide-react";
import { ROE_REASONS, EXPECTED_TO_RECALL, PAY_PERIOD_TYPES, LANGUAGES, PROVINCES } from "./knitData";

const L = ({ children }) => (
  <span className="mb-0.5 block font-mono text-[9px] uppercase tracking-widest text-white/40">{children}</span>
);
const inputCls = "h-8 w-full rounded-md border border-white/10 bg-white/[0.03] px-2 text-sm text-white/90 focus:border-[#1a3fd1] focus:outline-none";
const selCls = `${inputCls}`;

const PP_COUNT = 30;
const money = (n) => `$${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const Section = ({ title, children }) => (
  <div className="mt-6">
    <p className="mb-3 border-b border-white/10 pb-2 font-mono text-[11px] uppercase tracking-[0.18em] text-white/60">{title}</p>
    {children}
  </div>
);

export default function RoeCreate({ employee, roe, onBack, onSaved }) {
  const [busy, setBusy] = useState(false);
  const [autofill, setAutofill] = useState(null);
  const [company, setCompany] = useState({
    legal_name: "", address_line1: "", city: "", province: "", postal: "",
    cra_bn: "", payroll_ref: "", contact_first: "", contact_last: "",
    language: "English", area_code: "", phone: "", extension: "",
  });
  const nameParts = (employee.name || "").trim().split(/\s+/);
  const [info, setInfo] = useState({
    first_name: nameParts[0] || "", middle_initial: "", last_name: nameParts.slice(1).join(" ") || "",
    sin: employee.sin || "", job_title: employee.job_title || employee.trade || "",
    address_line1: employee.address_street || "", city: employee.address_city || "",
    province: employee.address_province || "Ontario", postal: employee.address_postal || "",
  });
  const [reason, setReason] = useState(employee.dismissal?.termination_reason || "");
  const [expectedRecall, setExpectedRecall] = useState("Unknown");
  const [comments, setComments] = useState(employee.dismissal?.notes || "");
  const [ins, setIns] = useState({
    first_day_worked: employee.hired_at || "",
    last_day_paid: employee.dismissal?.last_day || "",
    pay_period_type: "Weekly",
    final_period_end: employee.dismissal?.last_day || "",
    total_ins_hours: "", total_ins_earning: "",
    pp: Object.fromEntries(Array.from({ length: PP_COUNT }, (_, i) => [`PP${i + 1}`, ""])),
  });
  const [sep, setSep] = useState({ vacation: [], stat_holiday: [], other: [], special: [] });

  // Load company profile + existing draft.
  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/settings/company-profile");
        setCompany((c) => ({ ...c, ...data }));
      } catch { /* defaults are fine */ }
    })();
  }, []);
  useEffect(() => {
    if (!roe) return;
    if (roe.company) setCompany((c) => ({ ...c, ...roe.company }));
    if (roe.employee_info) setInfo((i) => ({ ...i, ...roe.employee_info }));
    setReason(roe.reason || "");
    setExpectedRecall(roe.expected_recall || "Unknown");
    setComments(roe.comments || "");
    if (roe.insurable) setIns((p) => ({ ...p, ...roe.insurable, pp: { ...p.pp, ...(roe.insurable.pp || {}) } }));
    if (roe.separation_payments) setSep((s) => ({ ...s, ...roe.separation_payments }));
  }, [roe]);

  const setC = (k) => (e) => setCompany((p) => ({ ...p, [k]: e.target.value }));
  const setI = (k) => (e) => setInfo((p) => ({ ...p, [k]: e.target.value }));
  const setInsField = (k) => (e) => setIns((p) => ({ ...p, [k]: e.target.value }));

  const ppTotal = useMemo(
    () => Object.values(ins.pp).reduce((s, v) => s + (parseFloat(v) || 0), 0),
    [ins.pp]
  );

  const oneClickAutofill = async () => {
    try {
      const { data } = await api.get(`/employees/${employee.employee_id}/roe-autofill`, {
        params: { last_day: ins.last_day_paid || undefined },
      });
      setAutofill(data);
      setIns((p) => {
        const pp = Object.fromEntries(Array.from({ length: PP_COUNT }, (_, i) => [`PP${i + 1}`, ""]));
        (data.periods || []).forEach((per, i) => {
          if (i < PP_COUNT) pp[`PP${i + 1}`] = per.insurable_amount ? String(per.insurable_amount) : "";
        });
        return {
          ...p, pp,
          first_day_worked: p.first_day_worked || data.first_day_worked || "",
          last_day_paid: p.last_day_paid || data.last_day_paid || "",
          final_period_end: p.final_period_end || data.last_day_paid || "",
          total_ins_hours: String(data.total_insurable_hours || ""),
          total_ins_earning: String(data.total_insurable_pay || ""),
        };
      });
      toast.success(`Autofilled ${data.number_of_pay_periods} pay periods from payroll history`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Autofill failed");
    }
  };

  const save = async (status) => {
    setBusy(true);
    try {
      // Persist the company block so the next ROE is prefilled.
      try { await api.put("/settings/company-profile", company); } catch { /* non-admin */ }
      const { data } = await api.post(`/employees/${employee.employee_id}/roes`, {
        roe_id: roe?.roe_id || undefined,
        status,
        company, employee_info: info, reason,
        expected_recall: expectedRecall, comments,
        insurable: { ...ins, total_ins_earning: ins.total_ins_earning || ppTotal.toFixed(2) },
        separation_payments: sep,
      });
      toast.success(status === "ready" ? "ROE saved — ready for review" : "ROE draft saved");
      onSaved?.(data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not save the ROE");
    } finally {
      setBusy(false);
    }
  };

  const SepSection = ({ title, k, testid }) => (
    <div className="border-b border-white/5 py-3">
      <p className="mb-2 text-sm text-white/70">{title}</p>
      {sep[k].map((row, i) => (
        <div key={i} className="mb-2 flex items-center gap-2">
          <input value={row.label} placeholder="Description"
            onChange={(e) => setSep((p) => ({ ...p, [k]: p[k].map((r, j) => (j === i ? { ...r, label: e.target.value } : r)) }))}
            className={`${inputCls} flex-1`} data-testid={`${testid}-label-${i}`} />
          <input value={row.amount} type="number" step="0.01" placeholder="0.00"
            onChange={(e) => setSep((p) => ({ ...p, [k]: p[k].map((r, j) => (j === i ? { ...r, amount: e.target.value } : r)) }))}
            className={`${inputCls} w-28`} data-testid={`${testid}-amount-${i}`} />
          <button onClick={() => setSep((p) => ({ ...p, [k]: p[k].filter((_, j) => j !== i) }))}
            aria-label="Remove" className="rounded p-1 text-white/40 hover:text-red-400"><X className="h-3.5 w-3.5" /></button>
        </div>
      ))}
      <button onClick={() => setSep((p) => ({ ...p, [k]: [...p[k], { label: "", amount: "" }] }))}
        data-testid={`${testid}-add`}
        className="inline-flex items-center gap-1 font-mono text-[10px] uppercase tracking-widest text-amber-500 hover:text-amber-400">
        <Plus className="h-3 w-3" /> Add payment
      </button>
    </div>
  );

  return (
    <div data-testid="roe-create">
      <button onClick={onBack} data-testid="roe-back"
        className="mb-2 inline-flex items-center gap-1 font-mono text-[10px] uppercase tracking-widest text-amber-500 hover:text-amber-400">
        <ChevronLeft className="h-3.5 w-3.5" /> Back to profile
      </button>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-xl font-bold text-white">CREATE ROE</h2>
          <p className="mt-1 text-sm text-white/60">ROE for: {employee.name}</p>
        </div>
        <div className="flex gap-2">
          <button onClick={() => save("draft")} disabled={busy} data-testid="roe-save-draft"
            className="rounded-md border border-white/20 px-4 py-2 font-mono text-[10px] font-bold uppercase tracking-widest text-white/80 hover:text-white disabled:opacity-50">
            Save Draft
          </button>
          <button onClick={() => save("ready")} disabled={busy} data-testid="roe-save-review"
            className="rounded-md bg-amber-600 px-4 py-2 font-mono text-[10px] font-bold uppercase tracking-widest text-white hover:bg-amber-500 disabled:opacity-50">
            Save &amp; Review
          </button>
        </div>
      </div>

      <Section title="Company Information">
        <div className="grid grid-cols-2 gap-3 md:grid-cols-3">
          <label className="block"><L>*CRA business number (BN)</L>
            <input value={company.cra_bn} onChange={setC("cra_bn")} data-testid="roe-cra-bn" className={inputCls} placeholder="809042484RP0001" /></label>
          <label className="block"><L>Employer's payroll reference number</L>
            <input value={company.payroll_ref} onChange={setC("payroll_ref")} data-testid="roe-payroll-ref" className={inputCls} /></label>
          <label className="block"><L>Language</L>
            <select value={company.language} onChange={setC("language")} data-testid="roe-language" className={selCls}>
              {LANGUAGES.map((l) => <option key={l} value={l}>{l}</option>)}
            </select></label>
          <label className="block"><L>*Contact person first name</L>
            <input value={company.contact_first} onChange={setC("contact_first")} data-testid="roe-contact-first" className={inputCls} /></label>
          <label className="block"><L>*Contact person last name</L>
            <input value={company.contact_last} onChange={setC("contact_last")} data-testid="roe-contact-last" className={inputCls} /></label>
          <div className="grid grid-cols-3 gap-2">
            <label className="block"><L>*Area code</L>
              <input value={company.area_code} onChange={setC("area_code")} data-testid="roe-area-code" className={inputCls} /></label>
            <label className="block"><L>*Phone number</L>
              <input value={company.phone} onChange={setC("phone")} data-testid="roe-phone" className={inputCls} /></label>
            <label className="block"><L>Extension</L>
              <input value={company.extension} onChange={setC("extension")} data-testid="roe-extension" className={inputCls} /></label>
          </div>
        </div>
      </Section>

      <Section title="Employee Information">
        <div className="grid grid-cols-2 gap-3 md:grid-cols-3">
          <label className="block"><L>*First name</L>
            <input value={info.first_name} onChange={setI("first_name")} data-testid="roe-first-name" className={inputCls} /></label>
          <label className="block"><L>Middle initial</L>
            <input value={info.middle_initial} onChange={setI("middle_initial")} data-testid="roe-middle-initial" className={inputCls} /></label>
          <label className="block"><L>*Last name</L>
            <input value={info.last_name} onChange={setI("last_name")} data-testid="roe-last-name" className={inputCls} /></label>
          <label className="block"><L>*Social insurance number</L>
            <input value={info.sin} onChange={setI("sin")} data-testid="roe-sin" className={`${inputCls} font-mono`} /></label>
          <label className="block"><L>Job title</L>
            <input value={info.job_title} onChange={setI("job_title")} data-testid="roe-job-title" className={inputCls} /></label>
          <label className="block"><L>*Address line 1</L>
            <input value={info.address_line1} onChange={setI("address_line1")} data-testid="roe-address" className={inputCls} /></label>
          <label className="block"><L>*City</L>
            <input value={info.city} onChange={setI("city")} data-testid="roe-city" className={inputCls} /></label>
          <label className="block"><L>*Province</L>
            <select value={info.province} onChange={setI("province")} data-testid="roe-province" className={selCls}>
              {PROVINCES.map((p) => <option key={p} value={p}>{p}</option>)}
            </select></label>
          <label className="block"><L>Postal code</L>
            <input value={info.postal} onChange={setI("postal")} data-testid="roe-postal" className={`${inputCls} font-mono`} /></label>
          <label className="block"><L>*Reason for issuing ROE</L>
            <select value={reason} onChange={(e) => setReason(e.target.value)} data-testid="roe-reason" className={selCls}>
              <option value="">Please select</option>
              {ROE_REASONS.map((r) => <option key={r} value={r}>{r}</option>)}
            </select></label>
          <label className="block"><L>Expected to recall</L>
            <select value={expectedRecall} onChange={(e) => setExpectedRecall(e.target.value)} data-testid="roe-recall" className={selCls}>
              {EXPECTED_TO_RECALL.map((r) => <option key={r} value={r}>{r}</option>)}
            </select></label>
          <label className="block md:col-span-3"><L>Comments</L>
            <textarea value={comments} onChange={(e) => setComments(e.target.value)} rows={2} data-testid="roe-comments"
              className="w-full rounded-md border border-white/10 bg-white/[0.03] px-2 py-1.5 text-sm text-white/90 focus:border-[#1a3fd1] focus:outline-none" /></label>
        </div>
      </Section>

      <Section title="Insurable Earnings">
        <div className="grid gap-6 lg:grid-cols-2">
          <div>
            <div className="grid grid-cols-2 gap-3">
              <label className="block"><L>*First day worked</L>
                <input type="date" value={ins.first_day_worked} onChange={setInsField("first_day_worked")} data-testid="roe-first-day" className={inputCls} /></label>
              <label className="block"><L>*Last day paid</L>
                <input type="date" value={ins.last_day_paid} onChange={setInsField("last_day_paid")} data-testid="roe-last-day" className={inputCls} /></label>
              <label className="block"><L>*Pay period type</L>
                <select value={ins.pay_period_type} onChange={setInsField("pay_period_type")} data-testid="roe-period-type" className={selCls}>
                  {PAY_PERIOD_TYPES.map((p) => <option key={p} value={p}>{p}</option>)}
                </select></label>
              <label className="block"><L>*Final pay period ending date</L>
                <input type="date" value={ins.final_period_end} onChange={setInsField("final_period_end")} data-testid="roe-final-period" className={inputCls} /></label>
              <label className="block"><L>*Total ins. hours</L>
                <input type="number" step="0.01" value={ins.total_ins_hours} onChange={setInsField("total_ins_hours")} data-testid="roe-total-hours" className={inputCls} /></label>
              <label className="block"><L>Total ins. earning</L>
                <input type="number" step="0.01" value={ins.total_ins_earning} onChange={setInsField("total_ins_earning")} data-testid="roe-total-earning" className={inputCls} placeholder={ppTotal ? ppTotal.toFixed(2) : "0.00"} /></label>
            </div>
            <p className="mb-2 mt-4 font-mono text-[9px] uppercase tracking-widest text-white/40">Insurable earnings per pay period</p>
            <button onClick={oneClickAutofill} data-testid="roe-autofill-btn"
              className="mb-3 rounded-md bg-amber-600 px-3 py-1.5 font-mono text-[9px] font-bold uppercase tracking-widest text-white hover:bg-amber-500">
              One-click Autofill
            </button>
            <div className="grid grid-cols-2 gap-2">
              {Array.from({ length: PP_COUNT }, (_, i) => `PP${i + 1}`).map((pp) => (
                <label key={pp} className="block">
                  <L>*{pp}</L>
                  <input type="number" step="0.01" value={ins.pp[pp]} data-testid={`roe-${pp.toLowerCase()}`}
                    onChange={(e) => setIns((p) => ({ ...p, pp: { ...p.pp, [pp]: e.target.value } }))}
                    className={inputCls} />
                </label>
              ))}
            </div>
          </div>
          <div>
            {autofill ? (
              <div className="rounded-lg border border-white/10 p-3" data-testid="roe-autofill-summary">
                <div className="mb-2 grid grid-cols-3 gap-2 font-mono text-[10px] uppercase tracking-widest text-white/50">
                  <span>Pay periods: <b className="text-white">{autofill.number_of_pay_periods}</b></span>
                  <span>Insurable pay: <b className="text-white">{money(autofill.total_insurable_pay)}</b></span>
                  <span>Insurable hours: <b className="text-white">{autofill.total_insurable_hours}</b></span>
                </div>
                <table className="w-full text-left text-xs">
                  <thead>
                    <tr className="border-b border-white/10 font-mono text-[9px] uppercase tracking-widest text-white/40">
                      <th className="px-2 py-1">Index</th><th className="px-2 py-1">Pay Period</th>
                      <th className="px-2 py-1">Frequency</th><th className="px-2 py-1 text-right">Ins. Amount</th>
                      <th className="px-2 py-1 text-right">Ins. Hours</th>
                    </tr>
                  </thead>
                  <tbody>
                    {autofill.periods.map((p) => (
                      <tr key={p.index} className="border-b border-white/5 last:border-0">
                        <td className="px-2 py-1 text-white/60">{p.index}</td>
                        <td className="px-2 py-1 font-mono text-white/80">{p.period_start} - {p.period_end}</td>
                        <td className="px-2 py-1 text-white/60">{p.frequency}</td>
                        <td className="px-2 py-1 text-right font-mono text-white">{money(p.insurable_amount)}</td>
                        <td className="px-2 py-1 text-right font-mono text-white/80">{p.insurable_hours.toFixed(1)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <p className="rounded-lg border border-dashed border-white/10 px-3 py-8 text-center text-xs text-white/30">
                Press One-click Autofill to pull the pay-period table from this employee's payroll history.
              </p>
            )}
          </div>
        </div>
      </Section>

      <Section title="Separation Payments">
        <SepSection title="Vacation Pay" k="vacation" testid="roe-sep-vacation" />
        <SepSection title="Statutory Holiday Pay" k="stat_holiday" testid="roe-sep-stat" />
        <SepSection title="Other Monies" k="other" testid="roe-sep-other" />
        <SepSection title="Special Payments" k="special" testid="roe-sep-special" />
      </Section>
    </div>
  );
}
